import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Page from "@insite/mobius/Page";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { getInterCom, getIntercomData, getInterComSettings, useDefaultIntercom } from "@rightstore/Services/IntercomService";
import * as React from "react";
import { ReactNode } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    isProductListPage: getCurrentPage(state).type === "ProductListPage",
    userName: state.context.session.userName,
    currentUserIsGuest: !state.context.session.isAuthenticated
});

const mapDispatchToProps = {};

interface PageLayoutStyledWrapperProps {
    isProductListPage: boolean;
}

const PageLayoutStyledWrapper = styled.div<PageLayoutStyledWrapperProps>`
    display: flex;
    min-height: 100vh;
    flex-direction: column;
    > main {
        background: transparent;
        flex: 1;
        padding-top: 160px;
        overflow: ${props => props.isProductListPage ? "visible" : "hidden"};
        ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(
            theme,
            [
                null,
                css`
                padding-top: 0;
            `,
                null,
                null,
                null,
            ],
            "max",
        )}
    @media print {
        display: block;
    }
`

const IntercomCustomButtonStyling = styled.div`
    display: block; 
    position: fixed; 
    right: 20px;
    bottom: 20px;   
    height: 40px; 
    padding: 0 16px; 
    cursor: pointer;
    border-radius: 5px; 
    line-height: 40px; 
    font-weight: 700; 
    font-size: 1rem; 
    text-transform: uppercase;
    z-index: 2;
    background-color: ${getColor("secondary")};
    color: #FFFFFF;    
    transition: background-color .3s ease;

    &:hover { 
        background-color: #f45609; 
    }
}
`

interface PageLayoutProps {
    showHeader: boolean;
    header: ReactNode;
    showCompactHeader: boolean;
    compactHeader: ReactNode;
    showBreadcrumbs: boolean;
    pageContent: ReactNode;
    showFooter: boolean;
    footer: ReactNode;
    showSignInOverlay?: boolean;
    signInOverlay?: ReactNode;
    showStorePickupOverlay?: boolean;
    storePickupOverlay?: ReactNode;
    showAddressBookOverlay?: boolean;
    addressBookOverlay?: ReactNode;
}


type Props = PageLayoutProps & HasHistory & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

// This doesn't currently support HMR
const PageLayout = ({
    showHeader,
    header,
    showBreadcrumbs,
    pageContent,
    showFooter,
    footer,
    showCompactHeader,
    compactHeader,
    isProductListPage,
    userName,
    currentUserIsGuest,

    // INFO: From new update but will not be used for now
    // showStorePickupOverlay,
    // storePickupOverlay,
    // showAddressBookOverlay,
    // addressBookOverlay,
}: Props) => {
    const IntercomSettings = getInterComSettings();
    const IntercomResult = getInterCom();
    const intercomUri = "/api/v1/intercom/";

    React.useEffect(() => {
        if (IntercomSettings && IntercomSettings.app_id) {
            if (!currentUserIsGuest) {
                fetch(intercomUri)
                    .then((response) => response.json())
                    .then((response) => {
                        getIntercomData(userName, response, IntercomSettings.app_id, IntercomResult);
                    });
            } else {
                useDefaultIntercom(IntercomSettings.app_id, IntercomResult);
            }
        }
    }, [IntercomSettings])

    return (
        <PageLayoutStyledWrapper isProductListPage >
            {showHeader && header}
            {showCompactHeader && compactHeader}
            {showBreadcrumbs && !isProductListPage && (
                <Page as="div">
                    <PageBreadcrumbs />
                </Page>
            )}
            {pageContent}
            {showFooter && footer}
            {!!IntercomSettings?.app_id && (
                <IntercomCustomButtonStyling className="intercom-trigger">{translate("Need help?")}</IntercomCustomButtonStyling>
            )}
        </PageLayoutStyledWrapper>
    );
};

export default connect(mapStateToProps)(withHistory(PageLayout));
